import { type Conversation, parseCursorJsonlLine } from "@threadbase-sh/scanner";
import { existsSync, watch as fsWatch, readdirSync, readFileSync, statSync } from "fs";
import { homedir } from "os";
import { basename, join } from "path";
import type { ConversationCache } from "./conversation-cache";
import { cursorAgentTranscriptsDir } from "./cursor-transcript-watch";
import type { CacheMetadataRepository } from "./db/repositories/cacheMetadata.repository";
import type { ConversationsRepository } from "./db/repositories/conversations.repository";
import type { ManagedSessionsRepository } from "./db/repositories/managed-sessions.repository";
import type { ProjectsRepository } from "./db/repositories/projects.repository";
import type { SessionsRepository } from "./db/repositories/sessions.repository";
import type { LiveSessionManager } from "./live-session-manager";
import { getLogger } from "./logger";
import { CLAUDE_CODE_PROVIDER, CODEX_CLI_PROVIDER, CURSOR_PROVIDER } from "./providers";
import type { ScannerManager } from "./scanner-manager";
import type { ConversationWatcher } from "./services/conversations/conversationWatcher";
import type { SessionStore } from "./session-store";
import type { WSHub } from "./ws-hub";

/**
 * How long a transcript watcher keeps waiting for the file its provider will
 * write. Not a measured number: it arrived as `// give up after 2 minutes` in
 * 76cdf9f2, when these watchers existed to re-key a placeholder session id to
 * the real UUID — a mechanism that no longer exists. It survives only as a
 * bound on an abandoned session's watch, and it is deliberately measured from
 * the user's first turn rather than from the spawn, because that turn is what
 * causes the file to exist. Both watchers re-arm it per turn and neither applies
 * it before the first one. See the notes in watchForJsonl/watchForCodexRollout.
 */
export const TRANSCRIPT_WATCH_DEADLINE_MS = 120_000;

/**
 * Everything SessionWatchers reads from the server. Collaborators constructed
 * once in the server constructor are passed by reference; the ones opened
 * during listen() (and rebound by the integrity monitor's reset-and-rescan) are
 * thunks, for the same reason ApiDeps passes `cache: () => ConversationCache | null`.
 *
 * `broadcastConversationLines` / `findConversationByUuid` / `ptyAttachedIds`
 * stay late-bound calls back into the server rather than moved code: they are
 * server methods with their own dependencies (and tests spy on them).
 */
export type SessionWatchersDeps = {
  ptyManager: LiveSessionManager;
  sessionStore: SessionStore;
  wsHub: WSHub;
  fileWatcher: ConversationWatcher;
  /** sessionId → JSONL filePath. Owned by the server; mutated in place here. */
  sessionFileMap: Map<string, string>;
  scannerManager: ScannerManager;
  codexRoots: string[];
  /** Cursor project roots (`~/.cursor/projects`); empty disables Cursor binding. */
  cursorRoots: string[];
  cache: () => ConversationCache | null;
  projectsRepo: () => ProjectsRepository | null;
  conversationsRepo: () => ConversationsRepository | null;
  sessionsRepo: () => SessionsRepository | null;
  cacheMetadataRepo: () => CacheMetadataRepository | null;
  managedSessionsRepo: () => ManagedSessionsRepository | null;
  findConversationByUuid: (uuid: string) => Promise<Conversation | null>;
  broadcastConversationLines: (
    sessionId: string,
    lines: string[],
    seqs?: (number | null)[] | null,
  ) => void;
  ptyAttachedIds: () => Set<string>;
};

/**
 * Binds a live session to the transcript file its provider writes: finds the
 * JSONL (Claude), rollout (Codex), or agent-transcripts run (Cursor), starts
 * the tail watcher, replays whatever was written before the watcher attached,
 * and links the session to its project.
 *
 * Extracted from StreamerServer so watcher work stops editing the server file
 * (see docs/plans/2026-07-12-server-ts-split.md, PR 5).
 */
export class SessionWatchers {
  private log = getLogger("server");

  constructor(private deps: SessionWatchersDeps) {}

  // ─── Project linking ─────────────────────────────────────────────

  linkSessionToProject(sessionId: string, projectPath: string, filePath: string): void {
    const projectsRepo = this.deps.projectsRepo();
    const conversationsRepo = this.deps.conversationsRepo();
    const sessionsRepo = this.deps.sessionsRepo();
    const cache = this.deps.cache();
    if (!projectsRepo || !conversationsRepo || !sessionsRepo || !cache) {
      return;
    }
    try {
      const project = projectsRepo.upsertProjectByPath(projectPath, {
        lastConversationId: sessionId,
        lastConversationCreatedAt: new Date().toISOString(),
      });
      // The conversation row may not exist yet (Claude is still writing the
      // JSONL). Best-effort: only link if the row is present.
      if (cache.hasConversation(sessionId)) {
        conversationsRepo.updateConversationProjectId({
          conversationId: sessionId,
          projectId: project.id,
        });
      }
      sessionsRepo.updateSessionProjectId({
        sessionId,
        projectId: project.id,
      });
      const cacheMetadataRepo = this.deps.cacheMetadataRepo();
      if (cacheMetadataRepo) {
        cacheMetadataRepo.setCacheMetadata("last_conversation_id", sessionId);
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      this.log.warn(`[projects] failed to link session to project: ${message}`, {
        event: "session.project_link_failed",
        sessionId,
        projectPath,
        filePath,
        error: message,
      });
    }
  }

  // ─── File Watcher Wiring ─────────────────────────────────────────

  // `historyId` is the id the provider filed the history under, which for a
  // fresh Codex session is its rollout id rather than our placeholder. The map
  // stays keyed by `sessionId` — that is what broadcasts resolve against.
  async watchConversationFile(sessionId: string, historyId = sessionId): Promise<void> {
    try {
      const conversation = await this.deps.findConversationByUuid(historyId);
      if (conversation?.filePath) {
        this.deps.sessionFileMap.set(sessionId, conversation.filePath);
        this.deps.fileWatcher.watch(conversation.filePath);
      }
    } catch {
      // Best-effort: if we can't find the JSONL file, raw terminal output still works
    }
  }

  // Read just the `sessionId` field from a JSONL's first line, used by the
  // watchForJsonl fallback to confirm a candidate file's identity before
  // binding it. Reads only up to the first newline so a large actively-written
  // file isn't slurped in full.
  readFirstLineSessionId(filePath: string): string | null {
    try {
      const content = readFileSync(filePath, "utf8");
      const nl = content.indexOf("\n");
      const firstLine = nl === -1 ? content : content.slice(0, nl);
      if (!firstLine.trim()) return null;
      const obj = JSON.parse(firstLine);
      return typeof obj.sessionId === "string" ? obj.sessionId : null;
    } catch {
      return null;
    }
  }

  // Watch the project directory for the JSONL file Claude creates for sessionId.
  // Once found, wire up structured event streaming. No rekeying needed — the UUID
  // was passed to Claude via --session-id so the filename matches from the start.
  watchForJsonl(sessionId: string, projectPath: string): void {
    const encoded = projectPath.replace(/[/\\:.]/g, "-");
    const projectsDir = join(homedir(), ".claude", "projects", encoded);
    const expectedFile = `${sessionId}.jsonl`;
    const filePath = join(projectsDir, expectedFile);
    const armedAt = Date.now();
    let deadline = armedAt + TRANSCRIPT_WATCH_DEADLINE_MS;
    let seenPrompts = 0;

    let watcher: ReturnType<typeof fsWatch> | null = null;
    const cleanup = () => {
      try {
        watcher?.close();
      } catch {
        /* ignore */
      }
    };

    const tryWire = () => {
      if (!this.deps.ptyManager.hasSession(sessionId)) {
        cleanup();
        return;
      }

      // Primary: Claude named the file after the session UUID
      let resolvedFilePath = existsSync(filePath) ? filePath : null;

      // Fallback: the `${sessionId}.jsonl` file hasn't appeared yet. Only bind a
      // candidate whose identity actually matches this session — its filename
      // stem OR its first-line `sessionId` field must equal our session id.
      // Claude 'resume' APPENDS to the SAME file with the SAME sessionId
      // (observed on Claude Code v2.1.215); the previous "resume writes a NEW
      // UUID file" assumption let this bind whichever JSONL was most recently
      // touched — capturing an actively-written FOREIGN conversation and
      // re-broadcasting its whole transcript under our session id. mtime is now
      // only a tiebreaker among already-matching candidates.
      if (!resolvedFilePath && existsSync(projectsDir)) {
        try {
          const now = Date.now();
          const match = readdirSync(projectsDir)
            .filter((f) => f.endsWith(".jsonl"))
            .map((f) => ({ f, mtime: statSync(join(projectsDir, f)).mtimeMs }))
            .filter(({ mtime }) => now - mtime < 5_000)
            .filter(
              ({ f }) =>
                basename(f, ".jsonl") === sessionId ||
                this.readFirstLineSessionId(join(projectsDir, f)) === sessionId,
            )
            .sort((a, b) => b.mtime - a.mtime)[0];
          if (match) resolvedFilePath = join(projectsDir, match.f);
        } catch {
          /* ignore */
        }
      }

      if (!resolvedFilePath) {
        // The deadline bounds how long we WAIT, not whether we accept a file
        // that has arrived. Claude writes <sessionId>.jsonl only on the user's
        // FIRST turn, so what this races is human think time: across a 20.5-day
        // production log the gap from pty.ready to the first prompt ran 3.6s to
        // 405.7s, and 17% of spawns were past 120s.
        //
        // So the deadline runs from the TURN, never from the spawn, and before
        // the first turn it does not run at all. Two separate reasons:
        //
        //  - Before any prompt the file CANNOT exist yet, so failing to find it
        //    is not evidence of anything and must not end the watch. This is
        //    what `fsWatch(projectsDir, …)` made dangerous: the handle is on the
        //    whole project directory (406 transcripts in one real project), so
        //    ANY neighbouring session writing its own JSONL fired tryWire, found
        //    ours absent, and closed our watch for good — after which the user's
        //    own first prompt bound nothing. The session then silently loses live
        //    line streaming; `locateJsonlPath` rung 4 still answers REST reads,
        //    which is why this never surfaced as a visible failure.
        //  - What the deadline was ever bounding is an ABANDONED session's watch,
        //    and abandonment is already covered above: `hasSession()` ends the
        //    watch when the PTY goes (hold, grace, or the 6h idle reaper).
        //
        // Past the first turn the deadline is meaningful again — there the file
        // is genuinely overdue — and it re-arms per turn, matching the identical
        // reasoning in watchForCodexRollout.
        const prompts = this.deps.sessionStore.getManaged(sessionId)?.promptCount ?? 0;
        if (prompts > seenPrompts) {
          seenPrompts = prompts;
          deadline = Date.now() + TRANSCRIPT_WATCH_DEADLINE_MS;
        }
        if (prompts === 0) return;
        if (Date.now() > deadline) {
          this.log.warn(
            `[startFresh] gave up watching for the JSONL of ${sessionId}`,
            {
              event: "session.transcript_watch_expired",
              sessionId,
              provider: CLAUDE_CODE_PROVIDER,
              projectPath,
              waitedMs: Date.now() - armedAt,
            },
            "pino",
          );
          cleanup();
        }
        return;
      }

      cleanup();
      this.deps.sessionFileMap.set(sessionId, resolvedFilePath);

      // Broadcast any lines already written before the watcher started — Claude
      // can finish writing the JSONL in the same tick as the watcher wires up,
      // so chokidar won't emit a change event for those lines. Dump BEFORE
      // starting the watcher: fileWatcher.watch() seeds its byte offset at the
      // file's current size, so seeding before the dump makes the next append
      // re-ship every dumped line (double broadcast). Seeding after the dump
      // means the watcher starts at the post-dump EOF.
      try {
        const existing = readFileSync(resolvedFilePath, "utf8").split("\n").filter(Boolean);
        if (existing.length > 0) {
          this.deps.broadcastConversationLines(sessionId, existing);
        }
      } catch {
        /* ignore — file may not be readable yet; watcher will catch future writes */
      }
      this.deps.fileWatcher.watch(resolvedFilePath);

      this.deps.scannerManager.markStaleOrDrop();
      this.linkSessionToProject(sessionId, projectPath, resolvedFilePath);
      this.deps.cache()?.markAsStreamer(sessionId);
      this.log.info(
        `[startFresh] wired JSONL for ${sessionId}`,
        { event: "session.jsonl_wired", sessionId, filePath: resolvedFilePath },
        "pino",
      );
    };

    tryWire();
    if (this.deps.sessionFileMap.has(sessionId)) return; // already found

    try {
      require("fs").mkdirSync(projectsDir, { recursive: true });
      watcher = fsWatch(projectsDir, tryWire);
      watcher.on("error", cleanup);
    } catch {
      // fs.watch not available (e.g. in tests), ignore
    }
  }

  // Codex-equivalent of watchForJsonl(). Differs because Codex has no
  // filename-encoded session id (it assigns its own persisted id) and its
  // rollout files live under a date-nested directory
  // (~/.codex/sessions/<YYYY>/<MM>/<DD>/rollout-*.jsonl) that Codex creates
  // itself — it may not exist yet when this function is first called, so we
  // poll rather than fs.watch a not-yet-existent directory.
  //
  // The rollout file is created LAZILY, on the user's first turn — not "within
  // ~1s of process spawn", as this comment claimed until the deadline was
  // investigated. Verified against Codex CLI 0.147.0 on a bound production
  // rollout: the filename and `session_meta.payload.timestamp` both read the
  // session-creation time (08:05:49) while the envelope `timestamp` of line 0
  // reads the first input (08:07:15). Every rollout binding observed in a
  // 20.5-day log landed ~100ms after the first input, never at spawn, with gaps
  // of 60.2s / 75.6s / 86.8s. So this races human think time, and unlike the
  // Claude side there is no recovery: `locateJsonlPath` has no rung that can
  // reconstruct `rollout-<ts>-<uuid>.jsonl` from a placeholder session id, and
  // an unbound Codex session also loses resume, fork and boot rehydration.
  watchForCodexRollout(sessionId: string, projectPath: string): void {
    let deadline = Date.now() + TRANSCRIPT_WATCH_DEADLINE_MS;
    let seenPrompts = 0;
    const now = new Date();
    const dateDir = join(
      String(now.getFullYear()),
      String(now.getMonth() + 1).padStart(2, "0"),
      String(now.getDate()).padStart(2, "0"),
    );

    // When this placeholder session started. Used to reject a stale same-cwd
    // rollout that Codex wrote before this session launched — the cwd match
    // alone can't tell a fresh rollout from a seconds-old one. 5s of slack
    // absorbs clock skew between our clock and Codex's session_meta timestamp.
    const sessionStartedAtMs =
      (this.deps.sessionStore.getManaged(sessionId)?.startedAt?.getTime() ?? Date.now()) - 5_000;

    let intervalHandle: ReturnType<typeof setInterval> | null = null;
    const cleanup = () => {
      if (intervalHandle) clearInterval(intervalHandle);
      intervalHandle = null;
    };

    // Read a candidate file's session_meta first line; accept only if its cwd
    // matches this session's projectPath and it was created at/after this
    // session started. Guards against picking up an unrelated concurrent Codex
    // session's rollout, or a stale same-cwd rollout from an earlier run, in
    // the same date-nested directory. Returns { id, createdAtMs } or null.
    const matchesProjectPath = (
      candidatePath: string,
    ): { id: string; createdAtMs: number } | null => {
      try {
        const firstLine = readFileSync(candidatePath, "utf8").split("\n", 1)[0];
        if (!firstLine) return null;
        const parsed = JSON.parse(firstLine);
        if (parsed?.type !== "session_meta") return null;
        const payload = parsed.payload ?? {};
        if (payload.cwd !== projectPath) return null;
        if (typeof payload.id !== "string") return null;
        // payload.timestamp is Codex's session-creation time; fall back to the
        // outer envelope timestamp if absent.
        const createdIso = payload.timestamp ?? parsed.timestamp;
        const createdAtMs = typeof createdIso === "string" ? Date.parse(createdIso) : Number.NaN;
        if (Number.isNaN(createdAtMs) || createdAtMs < sessionStartedAtMs) return null;
        return { id: payload.id, createdAtMs };
      } catch {
        return null;
      }
    };

    const tryWire = () => {
      if (!this.deps.ptyManager.hasSession(sessionId)) {
        cleanup();
        return;
      }
      // The turn is what creates the rollout, so the deadline runs from the
      // turn. An abandoned session still stops polling 120s after the spawn,
      // which is the only thing the deadline was ever bounding.
      // ponytail: promptCount moves only on POST /api/sessions/:id/input, so a
      // first prompt composed entirely of raw sendKeys keystrokes is not
      // covered — every first prompt in the log went through that endpoint.
      // Widen to a timestamp on the session if that ever stops being true.
      const prompts = this.deps.sessionStore.getManaged(sessionId)?.promptCount ?? 0;
      if (prompts > seenPrompts) {
        seenPrompts = prompts;
        deadline = Date.now() + TRANSCRIPT_WATCH_DEADLINE_MS;
      }
      if (Date.now() > deadline) {
        this.log.warn(
          `[startFresh] gave up watching for the Codex rollout of ${sessionId}`,
          {
            event: "session.transcript_watch_expired",
            sessionId,
            provider: CODEX_CLI_PROVIDER,
            projectPath,
            promptCount: prompts,
          },
          "pino",
        );
        cleanup();
        return;
      }

      // Codex ids already bound to another live placeholder — never bind two
      // placeholders to the same rollout (e.g. two Codex sessions started in
      // the same project inside the mtime window).
      const boundElsewhere = new Set(
        this.deps.sessionStore
          .listManaged()
          .filter((s) => s.id !== sessionId && s.boundConversationId != null)
          .map((s) => s.boundConversationId as string),
      );

      for (const root of this.deps.codexRoots) {
        const sessionsDir = join(root, dateDir);
        if (!existsSync(sessionsDir)) continue;

        let candidateFiles: string[];
        try {
          candidateFiles = readdirSync(sessionsDir).filter((f) => f.endsWith(".jsonl"));
        } catch {
          continue;
        }

        const nowMs = Date.now();
        const recentCandidates = candidateFiles
          .map((f) => ({ f, mtime: statSync(join(sessionsDir, f)).mtimeMs }))
          .filter(({ mtime }) => nowMs - mtime < 10_000)
          .sort((a, b) => b.mtime - a.mtime);

        for (const { f } of recentCandidates) {
          const candidatePath = join(sessionsDir, f);
          const match = matchesProjectPath(candidatePath);
          if (!match) continue;
          if (boundElsewhere.has(match.id)) continue;
          const codexSessionId = match.id;

          cleanup();
          this.deps.sessionStore.updateManaged(sessionId, {
            boundConversationId: codexSessionId,
          });
          // Durably too: after a restart the registry row is the only place
          // this binding survives, and it is the only id `codex resume` accepts.
          try {
            this.deps.managedSessionsRepo()?.recordBinding(sessionId, codexSessionId);
          } catch (err) {
            this.log.warn("[registry] failed to record Codex rollout binding", {
              event: "registry.binding_write_failed",
              sessionId,
              err,
            });
          }

          // Wire the bound rollout into the live update path: tail it for
          // structured events and replay anything already written before the
          // watcher attached (mirrors watchForJsonl()). Without this the bound
          // Codex JSONL is never live-streamed to clients.
          this.deps.sessionFileMap.set(sessionId, candidatePath);
          this.deps.fileWatcher.watch(candidatePath);
          try {
            const existing = readFileSync(candidatePath, "utf8").split("\n").filter(Boolean);
            if (existing.length > 0) {
              this.deps.broadcastConversationLines(sessionId, existing);
            }
          } catch {
            /* ignore — file may not be readable yet; watcher will catch future writes */
          }

          this.deps.scannerManager.markStaleOrDrop();
          this.linkSessionToProject(sessionId, projectPath, candidatePath);
          this.deps.cache()?.markAsStreamer(sessionId);

          // Push the binding to subscribers now — the async discovery means the
          // session_update at start time carried no boundConversationId.
          const resp = this.deps.sessionStore.get(sessionId, this.deps.ptyAttachedIds());
          if (resp) {
            this.deps.wsHub.broadcast({ type: "session_update", session: resp });
          }

          this.log.info(
            `[startFresh] bound Codex rollout for ${sessionId}`,
            {
              event: "session.codex_rollout_bound",
              sessionId,
              boundConversationId: codexSessionId,
              filePath: candidatePath,
            },
            "pino",
          );
          return;
        }
      }
    };

    tryWire();
    if (!intervalHandle && Date.now() <= deadline) {
      // Only keep polling if tryWire() didn't already find + cleanup() the match.
      const alreadyBound =
        this.deps.sessionStore.getManaged(sessionId)?.boundConversationId != null;
      if (!alreadyBound) {
        intervalHandle = setInterval(tryWire, 250);
      }
    }
  }

  // Cursor-equivalent of watchForCodexRollout(). Cursor mints its own run id
  // under `~/.cursor/projects/<slug>/agent-transcripts/<runId>/<runId>.jsonl`
  // and never writes our placeholder into the file (no `sessionId` field).
  // Without this bind, mobile keeps deep-linking to the placeholder while REST
  // history is indexed only under the run id — GET /api/conversations/:id 404s
  // after the empty-unused window closes. Same two-id shape as Codex:
  // `id`/`conversationId` stay the placeholder; `boundConversationId` is the
  // run id resume and history use.
  watchForCursorTranscript(sessionId: string, projectPath: string): void {
    if (this.deps.cursorRoots.length === 0) return;

    let deadline = Date.now() + TRANSCRIPT_WATCH_DEADLINE_MS;
    let seenPrompts = 0;
    const sessionStartedAtMs =
      (this.deps.sessionStore.getManaged(sessionId)?.startedAt?.getTime() ?? Date.now()) - 5_000;
    // Threadbase uploads for this PTY land under `.threadbase-uploads/<sessionId>/`
    // and Cursor's first user turn often @-mentions those paths — a strong signal
    // when several chats share one project slug.
    const uploadMarker = `.threadbase-uploads/${sessionId}/`;

    let intervalHandle: ReturnType<typeof setInterval> | null = null;
    const cleanup = () => {
      if (intervalHandle) clearInterval(intervalHandle);
      intervalHandle = null;
    };

    const tryWire = () => {
      if (!this.deps.ptyManager.hasSession(sessionId)) {
        cleanup();
        return;
      }

      const prompts = this.deps.sessionStore.getManaged(sessionId)?.promptCount ?? 0;
      if (prompts > seenPrompts) {
        seenPrompts = prompts;
        deadline = Date.now() + TRANSCRIPT_WATCH_DEADLINE_MS;
      }
      // Mirror Claude: the file cannot exist before the first turn, so failing
      // to find it then is not abandonment. Codex still expires from spawn; Cursor
      // follows the Claude contract because human think time was the production
      // failure mode (empty 200 → permanent 404 once promptCount > 0 and no bind).
      if (prompts === 0) return;
      if (Date.now() > deadline) {
        this.log.warn(
          `[startFresh] gave up watching for the Cursor transcript of ${sessionId}`,
          {
            event: "session.transcript_watch_expired",
            sessionId,
            provider: CURSOR_PROVIDER,
            projectPath,
            promptCount: prompts,
          },
          "pino",
        );
        cleanup();
        return;
      }

      const boundElsewhere = new Set(
        this.deps.sessionStore
          .listManaged()
          .filter((s) => s.id !== sessionId && s.boundConversationId != null)
          .map((s) => s.boundConversationId as string),
      );

      type Candidate = { runId: string; filePath: string; mtime: number; mentionsUpload: boolean };
      const candidates: Candidate[] = [];
      const nowMs = Date.now();

      for (const root of this.deps.cursorRoots) {
        const transcriptsDir = cursorAgentTranscriptsDir(root, projectPath);
        if (!existsSync(transcriptsDir)) continue;

        let runDirs: string[];
        try {
          runDirs = readdirSync(transcriptsDir, { withFileTypes: true })
            .filter((e) => e.isDirectory())
            .map((e) => e.name);
        } catch {
          continue;
        }

        for (const runId of runDirs) {
          if (boundElsewhere.has(runId)) continue;
          const filePath = join(transcriptsDir, runId, `${runId}.jsonl`);
          if (!existsSync(filePath)) continue;

          let mtime: number;
          try {
            mtime = statSync(filePath).mtimeMs;
          } catch {
            continue;
          }
          // Same recency window as Codex: the poll is continuous, so a just-
          // written file is always recent when it appears.
          if (nowMs - mtime >= 10_000) continue;
          if (mtime < sessionStartedAtMs) continue;

          let mentionsUpload = false;
          try {
            // Cap the read — we only need the opening user turn for the upload
            // path marker, not the whole transcript.
            const head = readFileSync(filePath, { encoding: "utf8" }).slice(0, 8_192);
            mentionsUpload = head.includes(uploadMarker);
          } catch {
            continue;
          }

          candidates.push({ runId, filePath, mtime, mentionsUpload });
        }
      }

      if (candidates.length === 0) return;

      candidates.sort((a, b) => {
        if (a.mentionsUpload !== b.mentionsUpload) return a.mentionsUpload ? -1 : 1;
        return b.mtime - a.mtime;
      });
      const match = candidates[0];

      cleanup();
      this.deps.sessionStore.updateManaged(sessionId, {
        boundConversationId: match.runId,
      });
      try {
        this.deps.managedSessionsRepo()?.recordBinding(sessionId, match.runId);
      } catch (err) {
        this.log.warn("[registry] failed to record Cursor transcript binding", {
          event: "registry.binding_write_failed",
          sessionId,
          err,
        });
      }

      this.deps.sessionFileMap.set(sessionId, match.filePath);
      this.deps.fileWatcher.watch(match.filePath);
      try {
        const existing = readFileSync(match.filePath, "utf8").split("\n").filter(Boolean);
        if (existing.length > 0) {
          // The replay starts at the top of the file, so each line's message
          // index is its count among the lines that parse — the numbering the
          // offset index and REST use. It goes into the Cursor uuid, which is
          // what lets the client match these against their history copies.
          let next = 0;
          const seqs = existing.map((l) => (parseCursorJsonlLine(l) ? next++ : null));
          this.deps.broadcastConversationLines(sessionId, existing, seqs);
        }
      } catch {
        /* ignore — file may not be readable yet; watcher will catch future writes */
      }

      this.deps.scannerManager.markStaleOrDrop();
      this.linkSessionToProject(sessionId, projectPath, match.filePath);
      this.deps.cache()?.markAsStreamer(sessionId);

      const resp = this.deps.sessionStore.get(sessionId, this.deps.ptyAttachedIds());
      if (resp) {
        this.deps.wsHub.broadcast({ type: "session_update", session: resp });
      }

      this.log.info(
        `[startFresh] bound Cursor transcript for ${sessionId}`,
        {
          event: "session.cursor_transcript_bound",
          sessionId,
          boundConversationId: match.runId,
          filePath: match.filePath,
        },
        "pino",
      );
    };

    tryWire();
    if (!intervalHandle) {
      const alreadyBound =
        this.deps.sessionStore.getManaged(sessionId)?.boundConversationId != null;
      if (!alreadyBound) {
        intervalHandle = setInterval(tryWire, 250);
      }
    }
  }
}
